import React, { useState, useEffect } from 'react';
import { Badge, Spinner, Row, Col, Form, Button } from 'react-bootstrap';
import { Briefcase, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { getHRPostedJobs } from '../../../../../services/contactServices.js';
import { getStudentAppliedJobs } from '../../../../../services/AppliedStudentsService.js';
import PlacementPopupView from '../../../placement/placementpopup/PlacementPopupView.jsx';

const JobsTab = ({ contact, organizations = [], users = [] }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [isStudent, setIsStudent] = useState(false);
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [limit] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // State for placement popup
  const [selectedPlacementForPopup, setSelectedPlacementForPopup] = useState(null);
  const [showPlacementPopup, setShowPlacementPopup] = useState(false);
  const [defaultActiveTab, setDefaultActiveTab] = useState('summary');

  const lastFetchedId = React.useRef(null);

  useEffect(() => {
    if (contact?.id) {
       // Reset and fetch when contact changes
       if (contact.id !== lastFetchedId.current) {
          setJobs([]);
          setCurrentPage(1);
          setHasMore(true);
          fetchJobs(1, true);
          lastFetchedId.current = contact.id;
       }
    }
  }, [contact?.id]);

  const fetchJobs = async (page = currentPage, isReset = false) => {
    setLoading(true);
    setError(null);
    try {
      // First try fetching HR posted jobs
      const hrResponse = await getHRPostedJobs(contact.id, page, limit);
      
      let newJobs = [];
      let total = 0;
      let totalPages = 0;

      if (hrResponse && hrResponse.data && Array.isArray(hrResponse.data) && hrResponse.data.length > 0) {
        newJobs = hrResponse.data;
        setIsStudent(false);
        if (hrResponse.pagination) {
          total = hrResponse.pagination.total || 0;
          totalPages = hrResponse.pagination.totalPages || 0;
        }
      } else {
        // If no HR jobs, try fetching student applied jobs
        const studentResponse = await getStudentAppliedJobs(contact.id, page);
        if (studentResponse && studentResponse.data && Array.isArray(studentResponse.data)) {
          newJobs = studentResponse.data;
          setIsStudent(true);
          if (studentResponse.pagination) {
            total = studentResponse.pagination.total || 0;
            totalPages = studentResponse.pagination.totalPages || 0;
          }
        }
      }

      if (isReset) {
        setJobs(newJobs);
        setFilteredJobs(newJobs);
      } else {
        setJobs(prev => [...prev, ...newJobs]);
        setFilteredJobs(prev => [...prev, ...newJobs]);
      }
      
      setTotalItems(total || (isReset ? newJobs.length : jobs.length + newJobs.length));
      setHasMore(totalPages ? page < totalPages : newJobs.length === limit);

    } catch (err) {
      console.error('Failed to load jobs:', err);
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleShowMore = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchJobs(nextPage);
  };

  const getAssignedHRPerson = (assignedToId) => {
    if (!assignedToId || !users.length) return null;
    return users.find((user) => user.id === assignedToId) || null;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'success', text: 'Active' },
      inactive: { bg: 'danger', text: 'Inactive' },
      closed: { bg: 'secondary', text: 'Closed' },
    };
    const config = statusConfig[status?.toLowerCase()] || { bg: 'secondary', text: status || 'Unknown' };
    return <Badge bg={config.bg} style={{ fontSize: '11px' }}>{config.text}</Badge>;
  };

  const getPositionStatusBadge = (positionStatus) => {
    const statusConfig = {
      open: { bg: 'info', text: 'Open' },
      filled: { bg: 'success', text: 'Filled' },
      closed: { bg: 'secondary', text: 'Closed' },
      onhold: { bg: 'warning', text: 'On Hold' },
    };
    const config = statusConfig[positionStatus?.toLowerCase()] || { bg: 'secondary', text: positionStatus || 'Unknown' };
    return <Badge bg={config.bg} style={{ fontSize: '11px' }}>{config.text}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getOrganizationName = (job) => {
    if (job.organizationName && !/^[0-9a-fA-F]{24}$/.test(job.organizationName)) {
      return job.organizationName;
    }
    
    const orgId = job.organizationId || (typeof job.organizationName === 'string' && /^[0-9a-fA-F]{24}$/.test(job.organizationName) ? job.organizationName : null);
    if (!orgId || !organizations.length) return job.organizationName || "N/A";
    
    const foundOrg = organizations.find(o => o.id === orgId);
    return foundOrg ? foundOrg.name : (job.organizationName || "N/A");
  };

  const handleViewPlacement = (placement) => {
    if (placement) {
      setSelectedPlacementForPopup(placement);
      setDefaultActiveTab('summary');
      setShowPlacementPopup(true);
    }
  };

  const handleClosePlacementPopup = () => {
    setShowPlacementPopup(false);
    setSelectedPlacementForPopup(null);
    setDefaultActiveTab('summary');
  };

  const handleUpdatePlacement = async (updatedPlacement) => {
    try {
      await fetchJobs();
      setSelectedPlacementForPopup(updatedPlacement);
    } catch (error) {
      console.error('Update failed:', error);
    }
  };

  // Search filter effect
  useEffect(() => {
    const trimmed = typeof searchTerm === 'string' ? searchTerm.trim() : '';
    if (!trimmed) {
      setFilteredJobs(jobs);
    } else {
      const lower = trimmed.toLowerCase();
      const filtered = jobs.filter(
        (job) =>
          job.jobName?.toLowerCase().includes(lower) ||
          job.organizationName?.toLowerCase().includes(lower) ||
          job.salary?.includes(trimmed) ||
          job.jobLocation?.toLowerCase().includes(lower)
      );
      setFilteredJobs(filtered);
    }
  }, [searchTerm, jobs]);

  if (loading) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '60px 20px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '300px'
      }}>
        <Spinner
          animation="border"
          style={{
            color: '#f97316',
            width: '2.5rem',
            height: '2.5rem',
            borderWidth: '3px'
          }}
        />
        <p style={{ marginTop: '16px', color: '#6c757d' }}>Loading jobs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '20px',
        margin: '20px',
        backgroundColor: '#f8d7da',
        border: '1px solid #f5c6cb',
        color: '#721c24',
        borderRadius: '4px'
      }}>
        {error}
      </div>
    );
  }

  return (
    <>
      <div className="jobs-content">
        <div className="jobs-header" style={{ marginBottom: "20px" }}>
          <h6 style={{ fontSize: "14px", fontWeight: "600", color: "#495057" }}>
            {isStudent ? `Jobs Applied by ${contact.name}` : `Jobs Posted by ${contact.name}`}
          </h6>
          <p style={{ fontSize: "12px", color: "#6c757d", marginBottom: 0 }}>
            Total Jobs: <Badge bg="primary">{totalItems}</Badge>
          </p>
        </div>

        {jobs.length > 0 && (
          <div className="mb-4 p-3 bg-light rounded">
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Search Jobs</Form.Label>
                  <div className="position-relative">
                    <Search
                      size={18}
                      className="position-absolute"
                      style={{ top: "50%", left: "12px", transform: "translateY(-50%)", color: "#6c757d" }}
                    />
                    <Form.Control
                      type="text"
                      placeholder="Search by job title, company, salary..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ paddingLeft: "40px" }}
                    />
                  </div>
                </Form.Group>
              </Col>
              <Col md={6} className="d-flex align-items-end">
                {searchTerm && (
                  <div className="d-flex align-items-center">
                    <small className="text-muted me-2">
                      Showing {filteredJobs.length} of {jobs.length} jobs
                    </small>
                    <Button variant="link" size="sm" onClick={() => setSearchTerm("")} className="p-0">
                      Clear
                    </Button>
                  </div>
                )}
              </Col>
            </Row>
          </div>
        )}

        {filteredJobs.length > 0 ? (
          <div className="table-responsive">
            <table className="table table-hover table-striped table-bordered table-sm">
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Job Title</th>
                  <th style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Organization</th>
                  <th style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Salary</th>
                  <th style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Location</th>
                  <th style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Posted Date</th>
                  <th style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Status</th>
                  <th style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Position</th>
                  <th style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredJobs.map((job, index) => (
                  <tr key={job.id || index}>
                    <td>
                      <div className="d-flex align-items-center">
                        <div
                          className="rounded-circle text-white d-flex align-items-center justify-content-center me-2"
                          style={{
                            width: "32px",
                            height: "32px",
                            fontSize: "12px",
                            backgroundColor: '#ea580c',
                            flexShrink: 0
                          }}
                        >
                          {job.jobName?.substring(0, 2)?.toUpperCase() || "JB"}
                        </div>
                        <div>
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start"
                            onClick={() => handleViewPlacement(job)}
                            style={{ textDecoration: "none" }}
                            title="View job details"
                          >
                            <div className="fw-semibold text-primary" style={{ fontSize: '13px' }}>
                              {job.jobName || "N/A"}
                            </div>
                          </button>
                        </div>
                      </div>
                    </td>
                    <td>
                        <div className="small" style={{ fontSize: "0.855rem" }}>
                        {getOrganizationName(job)}
                      </div>
                    </td>
                    <td>
                      <div className="small" style={{ fontSize: "0.855rem", fontWeight: '500' }}>
                        {job.salary || "Not Specified"}
                      </div>
                    </td>
                    <td>
                      <div className="small" style={{ fontSize: "0.855rem" }}>
                        {job.jobLocation || "Not specified"}
                      </div>
                    </td>
                    <td>
                      <div className="small" style={{ fontSize: "0.855rem" }}>
                        {formatDate(job.jobPostedDate)}
                      </div>
                    </td>
                    <td>
                      {getStatusBadge(job.status)}
                    </td>
                    <td>
                      {getPositionStatusBadge(job.jobPositionStatus)}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleViewPlacement(job)}
                          className="p-1"
                          title="View job details"
                          style={{ textDecoration: 'none', color: '#0d6efd' }}
                        >
                          View
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {hasMore && (
              <div className="d-flex justify-content-center mt-3">
                <Button 
                  variant="primary" 
                  size="sm" 
                  onClick={handleShowMore} 
                  disabled={loading}
                  style={{
                    backgroundColor: '#ea580c',
                    borderColor: '#ea580c',
                    padding: '8px 24px',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Loading...
                    </>
                  ) : (
                    'Show More'
                  )}
                </Button>
              </div>
            )}
            <small className="text-muted d-block mt-2 text-center">
              Showing {jobs.length} of {totalItems} jobs
            </small>
          </div>
        ) : jobs.length === 0 && !loading ? (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "300px",
            }}
          >
            <div className="mb-3">
              <Briefcase size={48} style={{ color: '#6c757d' }} />
            </div>
            <h6 style={{ color: '#6c757d' }}>{isStudent ? 'No Jobs Applied' : 'No Jobs Posted'}</h6>
            <p style={{ color: '#6c757d' }}>{isStudent ? 'This student has not applied for any jobs yet.' : 'This contact has not posted any jobs yet.'}</p>
          </div>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "300px",
            }}
          >
            <div className="mb-3">
              <Search size={48} style={{ color: '#6c757d' }} />
            </div>
            <h6 style={{ color: '#6c757d' }}>No Results Found</h6>
            <p style={{ color: '#6c757d' }}>No jobs match your search criteria.</p>
          </div>
        )}

        <style>{`
          .jobs-content {
            padding: 20px;
            background: white;
          }

          .jobs-header {
            border-bottom: 2px solid #f0f0f0;
            padding-bottom: 12px;
          }

          .table-responsive {
            border-radius: 6px;
            overflow: hidden;
            border: 1px solid #dee2e6;
          }

          .table {
            margin-bottom: 0;
            font-size: 0.875rem;
          }

          .table thead th {
            background-color: #f8f9fa;
            border-bottom: 2px solid #dee2e6;
            color: #495057;
            padding: 12px 8px;
            vertical-align: middle;
            letter-spacing: 0.5px;
          }

          .table tbody td {
            padding: 12px 8px;
            border-color: #dee2e6;
            vertical-align: middle;
          }

          .table tbody tr:hover {
            background-color: #f8f9fa;
          }

          .btn-link {
            color: #0d6efd !important;
            text-decoration: none;
          }

          .btn-link:hover {
            text-decoration: underline;
          }

          @media (max-width: 768px) {
            .jobs-content {
              padding: 15px;
            }

            .table {
              font-size: 11px;
            }

            .table thead th,
            .table tbody td {
              padding: 8px 4px;
            }
          }
        `}</style>
      </div>

      {selectedPlacementForPopup && (
        <PlacementPopupView
          show={showPlacementPopup}
          onHide={handleClosePlacementPopup}
          placement={selectedPlacementForPopup}
          onUpdatePlacement={handleUpdatePlacement}
          defaultTab={defaultActiveTab}
        />
      )}
    </>
  );
};

export default JobsTab;
